// Thunks for the flag-gated New Season editors. They write the
// new_season_data slice; V1 state (distribution_data) is only re-pulled after
// a write that changes what the dashboard shows (refreshDashboard).
import {
  get_v2_distributions,
  get_season,
  get_farming_unit_tree,
  put_farming_unit_layout,
  put_season,
  v2_error_message,
  post_pre_season,
  post_activate_pre_season,
} from '../../managers/newSeasonManager';
import { delete_field } from '../../managers/fieldManager';
import {
  reload_after_local_write,
  selected_entities_by_id,
} from './distributionActions';
const {
  isDashboardSeason,
  fieldToSelectAfterActivation,
} = require('../../utils/newSeasonDashboardRefresh');

export const newSeasonTreeKey = (distribution_id, farming_unit_id) =>
  distribution_id + '/' + farming_unit_id;

// These writes change the V1 tree the dashboard reads (the API writes both
// tables): re-pull it in the background, as V1's own writes do, so the
// dashboard is current without a page refresh. Never blocks or fails the
// write it follows.
const refreshDashboard = (dispatch, distribution_id) =>
  Promise.resolve(dispatch(reload_after_local_write(distribution_id))).catch(
    err => console.error('[New Season] dashboard refresh failed:', err)
  );

const tree_success = (tree_key, tree) => ({
  type: 'NEW_SEASON_TREE_SUCCESS',
  tree_key,
  tree,
});

export var load_new_season_tree = (distribution_id, farming_unit_id) => {
  return dispatch => {
    const tree_key = newSeasonTreeKey(distribution_id, farming_unit_id);
    dispatch({ type: 'NEW_SEASON_TREE_INIT', tree_key });
    return get_farming_unit_tree(distribution_id, farming_unit_id).then(
      tree => {
        dispatch(tree_success(tree_key, tree));
        return tree;
      },
      err => {
        dispatch({
          type: 'NEW_SEASON_TREE_FAIL',
          tree_key,
          error: v2_error_message(err),
        });
      }
    );
  };
};

// The layout PUT returns only {saved, farming_unit_id}; on success the tree is
// re-fetched with GET .../tree. Resolves with the new tree; rejects with a
// user-facing message (a failed re-fetch after a successful save rejects too,
// with saved: true, so the UI can tell the user to reload).
export var save_new_season_layout = (
  distribution_id,
  farming_unit_id,
  body
) => {
  return dispatch => {
    const tree_key = newSeasonTreeKey(distribution_id, farming_unit_id);
    return put_farming_unit_layout(distribution_id, farming_unit_id, body).then(
      () => {
        // Deleted blocks, names and groups show on the dashboard.
        refreshDashboard(dispatch, distribution_id);
        return get_farming_unit_tree(distribution_id, farming_unit_id).then(
          tree => {
            dispatch(tree_success(tree_key, tree));
            return tree;
          },
          err => Promise.reject({ saved: true, message: v2_error_message(err) })
        );
      },
      err => Promise.reject({ saved: false, message: v2_error_message(err) })
    );
  };
};

// Resolves with the saved season; rejects with a user-facing message.
export var save_new_season_season = (
  distribution_id,
  farming_unit_id,
  season_id,
  body
) => {
  return dispatch => {
    const tree_key = newSeasonTreeKey(distribution_id, farming_unit_id);
    return put_season(distribution_id, farming_unit_id, season_id, body).then(
      result => {
        if (result?.season) {
          dispatch({
            type: 'NEW_SEASON_SEASON_SAVED',
            tree_key,
            season: result.season,
          });
        }
        if (isDashboardSeason(result?.season)) {
          refreshDashboard(dispatch, distribution_id);
        }
        return result?.season;
      },
      err => Promise.reject(v2_error_message(err))
    );
  };
};

// GET v2/distributions into new_season_data.distributions. Resolves with the
// distributions array (or null on failure).
export var load_new_season_distributions = () => {
  return dispatch => {
    dispatch({ type: 'NEW_SEASON_DISTRIBUTIONS_INIT' });
    return get_v2_distributions().then(
      result => {
        const distributions = result?.distributions || [];
        dispatch({ type: 'NEW_SEASON_DISTRIBUTIONS_SUCCESS', distributions });
        return distributions;
      },
      err => {
        dispatch({
          type: 'NEW_SEASON_DISTRIBUTIONS_FAIL',
          error: v2_error_message(err),
        });
        return null;
      }
    );
  };
};

// GET v2/seasons/{d}/{fu}/{season}: resolves with {season, block, field_group}
// (season.sensors included); rejects with a user-facing message.
export var load_new_season_season_detail = (
  distribution_id,
  farming_unit_id,
  season_id
) => {
  return () =>
    get_season(distribution_id, farming_unit_id, season_id).catch(err =>
      Promise.reject(v2_error_message(err))
    );
};

// Create a block's pre-season. Resolves with the new season; rejects with a
// user-facing message.
export var create_new_season_pre_season = (
  distribution_id,
  farming_unit_id,
  body
) => {
  return () =>
    post_pre_season(distribution_id, farming_unit_id, body).then(
      result => result?.season,
      err => Promise.reject(v2_error_message(err))
    );
};

// After "Activate now": re-pull the dashboard's tree; when the dashboard had
// the now-archived season selected, select the new active one once it is in.
const refreshDashboardAfterActivation = (
  dispatch,
  distribution_id,
  farming_unit_id,
  selectedFieldId,
  activation
) => {
  const fieldId = fieldToSelectAfterActivation(selectedFieldId, activation);
  return refreshDashboard(dispatch, distribution_id).then(() => {
    if (fieldId) {
      dispatch(
        selected_entities_by_id(distribution_id, farming_unit_id, fieldId)
      );
    }
  });
};

// Delete an active or archived season: V1's DELETE /field, as the dashboard's
// Delete (pre-seasons are not in V1's table). Resolves once deleted; rejects with
// a user-facing message. The caller re-reads the tree.
export var delete_new_season_season = (
  distribution_id,
  farming_unit_id,
  season_id
) => {
  return dispatch =>
    delete_field(distribution_id, farming_unit_id, season_id).then(
      () => {
        refreshDashboard(dispatch, distribution_id);
      },
      err => Promise.reject(v2_error_message(err))
    );
};

// "Activate now": resolves with {season_id, archived_season_ids} once the
// tree is re-read; rejects with a user-facing message.
export var activate_new_season_pre_season = (
  distribution_id,
  farming_unit_id,
  season_id,
  body
) => {
  return (dispatch, getState) => {
    // Read before the write: the dashboard's re-pull reselects on its own.
    const selectedFieldId =
      getState().distribution_data?.selected_entities?.field?.id ?? null;
    return post_activate_pre_season(
      distribution_id,
      farming_unit_id,
      season_id,
      body
    ).then(
      result => {
        refreshDashboardAfterActivation(
          dispatch,
          distribution_id,
          farming_unit_id,
          selectedFieldId,
          result
        );
        return Promise.resolve(
          dispatch(load_new_season_tree(distribution_id, farming_unit_id))
        ).then(() => result);
      },
      err => Promise.reject(v2_error_message(err))
    );
  };
};
